import {
  isSecretValue,
  isTextValue,
  Secret,
  TextOrSecretId
} from '@kindo/universal';

import {
  Button,
  ButtonType,
  Select,
  Size,
  TextField,
  TextFieldType,
  Typography,
  TypographySize
} from '../../../../core';

interface WorkflowApiActionHeaderValueInputProps {
  disabled: boolean;
  onChange: (value: TextOrSecretId) => void;
  secrets: Pick<Secret, 'id' | 'label'>[];
  value: TextOrSecretId | undefined;
  placeholder?: string;
  prefix?: string;
}

enum HeaderValueType {
  SECRET = 'SECRET',
  TEXT = 'TEXT'
}

// Split from enum to avoid reliance on enum order
const HEADER_VALUE_TYPES = [HeaderValueType.TEXT, HeaderValueType.SECRET];

const WorkflowApiActionHeaderValueInput = ({
  disabled,
  value,
  onChange,
  secrets = [],
  placeholder = 'Value',
  prefix = ''
}: WorkflowApiActionHeaderValueInputProps) => {
  const isSecret = isSecretValue(value);
  const valueType = isSecret ? HeaderValueType.SECRET : HeaderValueType.TEXT;

  const handleValueTypeChange = (newType: HeaderValueType) => {
    if (newType === HeaderValueType.TEXT) {
      onChange({ text: '' });
    } else {
      onChange({ secretId: '' });
    }
  };

  const handleTextChange = (newValue: string) => {
    onChange({ text: newValue });
  };

  const handleSecretChange = (newValue: string) => {
    onChange({ secretId: newValue });
  };

  const getSecretLabel = () => {
    if (disabled) {
      return "CREATOR'S SECRET";
    }
    if (isSecretValue(value)) {
      return value.secretId;
    }
    return '';
  };

  return (
    <div
      className="flex w-full flex-row items-center gap-4"
      data-testid="workflow-api-action-header-value-input-container"
    >
      <div
        className="flex flex-row items-center gap-2"
        data-testid="workflow-api-action-header-button-group"
      >
        {HEADER_VALUE_TYPES.map((type) => (
          <Button
            disabled={disabled}
            key={type}
            label={type}
            onClick={() => handleValueTypeChange(type)}
            size={Size.SMALL}
            type={valueType === type ? ButtonType.SOLID : ButtonType.OUTLINED}
          />
        ))}
      </div>
      {valueType === HeaderValueType.TEXT ? (
        <TextField
          disabled={disabled}
          fullWidth
          onChange={handleTextChange}
          placeholder={placeholder}
          prefix={prefix}
          type={
            !isSecret && isTextValue(value)
              ? TextFieldType.DEFAULT
              : TextFieldType.HIGHLIGHT
          }
          value={isTextValue(value) ? value.text : ''}
        />
      ) : (
        <>
          {prefix && (
            <Typography size={TypographySize.SMALL}>{prefix}</Typography>
          )}
          <Select
            buttonSize={Size.SMALL}
            disabled={disabled || !secrets || secrets.length === 0}
            onChange={handleSecretChange}
            options={secrets.map((secret) => ({
              label: secret.label,
              value: secret.id
            }))}
            placeholderLabel="Select Secret"
            tooltip={
              !disabled && (!secrets || secrets.length === 0)
                ? 'Create a secret in Settings > Secrets Vault to reference here'
                : undefined
            }
            type={
              isSecret && value.secretId
                ? ButtonType.OUTLINED
                : ButtonType.OUTLINED_COLOR
            }
            value={getSecretLabel()}
          />
        </>
      )}
    </div>
  );
};

export default WorkflowApiActionHeaderValueInput;
